/* global window */

import log from 'loglevel';
import sha256 from 'tiny-sha256';

import Sailthru from './Sailthru';
import Braze from './Braze';
import PIP from './PIP';
import MediaOS from './MediaOS';
import {
  getParameterByName,
  isEmailValid,
} from './utils';


/**
 * Sync user ID before starting a Braze session.
 * @param {Sailthru} sailthru - Sailthru instance.
 * @param {Braze} braze - Braze instance.
 * @param {PIP} pip - PIP instance.
 */
function syncUserVeryLocal(sailthru, braze, pip) {
  if (!sailthru.isSailthruReady || !braze.isBrazeReady) {
    return;
  }

  sailthru.fetchCurrentUserData()
    .then(() => braze.changeUser(sailthru.userEmailHash))
    .then(() => braze.fetchCurrentUserData())
    .then(() => braze.openSession())
    .then(() => pip.getUserLocationPermissionStatus())
    .then(() => pip.getUserLocation())
    .then(() => pip.getUserNeighborhoods())
    .then(() => braze.setLastKnownLocation(pip.location, pip.neighborhoods))
    .then(() => braze.pushRequest())
    .catch(error => log.error(error));
}

/**
 * Sync user ID before starting a Braze session.
 * @param {Sailthru} sailthru - Sailthru instance.
 * @param {Braze} braze - Braze instance.
 * @param {PIP} pip - PIP instance.
 */
function syncUserMediaOS(sailthru, braze, pip) {
  if (!sailthru.isSailthruReady || !braze.isBrazeReady) {
    return;
  }

  sailthru.fetchCurrentUserData()
    .then(() => braze.changeUser(sailthru.userEmailHash))
    .then(() => braze.fetchCurrentUserData())
    .then(() => braze.openSession())
    .then(() => pip.getUserLocationPermissionStatus())
    .then(() => pip.getUserLocation())
    .then(() => pip.getUserNeighborhoods())
    .then(() => braze.setLastKnownLocation(pip.location, pip.neighborhoods))
    .then(() => MediaOS.updateWeather(pip.location))
    .then(() => braze.pushRequest())
    .catch(error => log.error(error));
}

/**
 * Signup user to Braze email if query params are present
 * @param {Braze} braze - Braze instance.
 */
function signUpBrazeViaQueryString(braze) {
  if (!braze.isBrazeReady) {
    return;
  }

  const email = getParameterByName('eml');
  const groupId = getParameterByName('sub_grp_id');

  if (email && groupId) {
    if (isEmailValid(email)) {
      braze.userSignUp(sha256(email), email, groupId, 'click-to-subscribe');
    }
  }
}

/**
 * Basic user signup flow: subscribe the email to the Sailthru list
 * and change the Braze user id to the email hash.
 * @param {Sailthru} sailthru - Sailthru instance.
 * @param {Braze} braze - Braze instance.
 * @param {string} email - User email.
 * @param {string} list - Sailthru email list.
 * @param {string} source - Sailthru subscription source.
 * @param {Object} vars - Sailthru signup variables.
 * @returns {Promise}
 */
function userSignUp(sailthru, braze, email, list, source, vars = {}) {
  const lists = {};
  lists[list] = 1;

  return Promise.all([
    sailthru.userSignUp(email, lists, source, vars),
    braze.changeUser(sha256(email)),
  ]);
}

/**
 * Basic user signup flow: change user and subscribe the email to the Braze list
 * @param {Braze} braze - Braze instance.
 * @param {string} email - User email.
 * @param {string} groupId - Braze email list.
 * @param {string} source - subscription source.
 * @returns {Promise}
 */
function userSignUpBraze(braze, email, groupId, source) {
  return Promise.all([
    braze.userSignUp(sha256(email), email, groupId, source),
  ]);
}

/**
 * Load and initialize required scripts.
 */
function bootstrap() {
  const sailthru = new Sailthru();
  const braze = new Braze();
  const pip = new PIP();
  const components = [sailthru, braze, pip];

  if (window.METAPHOR_DISABLED) {
    return;
  }

  Promise.all(components.map(component => component.load()))
    .then(() => {
      components.map(component => component.init());

      if (window.VLCL) {
        syncUserVeryLocal(sailthru, braze, pip);
      }

      if (window.HRST) {
        syncUserMediaOS(sailthru, braze, pip);
        signUpBrazeViaQueryString(braze);
      }

      window.Metaphor = window.Metaphor || {};
      window.Metaphor.signUp = userSignUp.bind(this, sailthru, braze);
      window.Metaphor.signUpBraze = userSignUpBraze.bind(this, braze);
    })
    .catch(error => log.error(error));
}

export default bootstrap;



// WEBPACK FOOTER //
// ./src/bootstrap.js